/**
 * AddTeamMemberDialog
 * Add/remove project team members with anti-orphan rules:
 * - Project must always keep ≥1 member
 * - Removing a member with task RACI opens MemberReassignmentDialog
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search, Users } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Project, Task, User } from '@/types';
import { userService, projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import MemberReassignmentDialog, {
  type PendingRemoval,
} from './MemberReassignmentDialog';

interface AddTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  tasks?: Task[];
}

function userDisplayName(user: User): string {
  return (
    user.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email
  );
}

function userHasTaskRaci(task: Task, userId: string): boolean {
  const raci = task.raci;
  if (!raci) return false;
  if (raci.accountable === userId) return true;
  if (Array.isArray(raci.responsible) && raci.responsible.includes(userId)) return true;
  if (Array.isArray(raci.consulted) && raci.consulted.includes(userId)) return true;
  if (Array.isArray(raci.informed) && raci.informed.includes(userId)) return true;
  return false;
}

function countUserRaciTasks(tasks: Task[], userId: string): number {
  return tasks.filter((t) => userHasTaskRaci(t, userId)).length;
}

const AddTeamMemberDialog: React.FC<AddTeamMemberDialogProps> = ({
  open,
  onClose,
  project,
  tasks = [],
}) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(project.teamMembers || [])
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [replacementStepOpen, setReplacementStepOpen] = useState(false);
  const [pendingRemovals, setPendingRemovals] = useState<PendingRemoval[]>([]);
  const [replacements, setReplacements] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedUserIds(new Set(project.teamMembers || []));
      setErrorMessage(null);
      setReplacementStepOpen(false);
      setPendingRemovals([]);
      setReplacements({});
    }
  }, [open, project.teamMembers]);

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: [...QUERY_KEYS.users, 'include-admins'],
    queryFn: () => userService.getUsers(undefined, { pageSize: 100, includeAdmins: true }),
  });

  const users = usersData || [];
  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter((user) => {
      const hay = [
        user.displayName,
        user.firstName,
        user.lastName,
        user.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(query);
    });
  }, [users, searchQuery]);

  const updateProjectMutation = useMutation({
    mutationFn: (payload: {
      teamMemberIds: string[];
      memberReplacements?: Record<string, string>;
    }) =>
      projectService.updateProject(project.id, {
        teamMemberIds: payload.teamMemberIds,
        memberReplacements: payload.memberReplacements,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projects, project.id] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      setReplacementStepOpen(false);
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response
          ?.data?.message ||
        (err as Error)?.message ||
        'Failed to update team members';
      setErrorMessage(message);
    },
  });

  const currentTeamIds = useMemo(
    () => new Set(project.teamMembers || []),
    [project.teamMembers]
  );
  const addedCount = Array.from(selectedUserIds).filter((id) => !currentTeamIds.has(id)).length;
  const removedIds = Array.from(currentTeamIds).filter((id) => !selectedUserIds.has(id));
  const removedCount = removedIds.length;
  const hasChanges = addedCount > 0 || removedCount > 0;

  const handleToggleUser = (userId: string) => {
    setErrorMessage(null);
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        if (next.size <= 1) {
          setErrorMessage('Project must have at least one team member');
          return prev;
        }
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const submitWithReplacements = (memberReplacements?: Record<string, string>) => {
    updateProjectMutation.mutate({
      teamMemberIds: Array.from(selectedUserIds),
      memberReplacements,
    });
  };

  const handleSave = () => {
    setErrorMessage(null);
    const teamMemberIds = Array.from(selectedUserIds);
    if (teamMemberIds.length < 1) {
      setErrorMessage('Project must have at least one team member');
      return;
    }

    const needingReplacement = removedIds
      .map((userId) => ({
        userId,
        taskCount: countUserRaciTasks(tasks, userId),
      }))
      .filter((r) => r.taskCount > 0);

    if (needingReplacement.length > 0) {
      const initial: Record<string, string> = {};
      needingReplacement.forEach((r) => {
        initial[r.userId] = teamMemberIds.find((id) => id !== r.userId) || '';
      });
      setPendingRemovals(needingReplacement);
      setReplacements(initial);
      setReplacementStepOpen(true);
      return;
    }

    submitWithReplacements();
  };

  const handleConfirmReplacements = () => {
    for (const item of pendingRemovals) {
      const replacementId = replacements[item.userId];
      if (!replacementId) {
        setErrorMessage('Select a replacement for every member who has task assignments');
        return;
      }
      if (!selectedUserIds.has(replacementId)) {
        setErrorMessage('Replacement must be a remaining project team member');
        return;
      }
    }
    setErrorMessage(null);
    submitWithReplacements(replacements);
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUserIds(new Set(project.teamMembers || []));
    setErrorMessage(null);
    setReplacementStepOpen(false);
    setPendingRemovals([]);
    setReplacements({});
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !replacementStepOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={24} />
            <Typography variant="h6">Manage Team Members</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Add or remove team members from {project.name}. At least one member is required.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />

          {errorMessage && !replacementStepOpen && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {hasChanges && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {addedCount > 0 && (
                  <Chip label={`+${addedCount} Added`} color="success" size="small" variant="outlined" />
                )}
                {removedCount > 0 && (
                  <Chip label={`-${removedCount} Removed`} color="error" size="small" variant="outlined" />
                )}
              </Box>
            </Alert>
          )}

          {isLoadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No users found matching your search' : 'No users available'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredUsers.map((user) => {
                const isSelected = selectedUserIds.has(user.id);
                const displayName = userDisplayName(user);
                const raciTaskCount = currentTeamIds.has(user.id)
                  ? countUserRaciTasks(tasks, user.id)
                  : 0;

                return (
                  <ListItem key={user.id} dense disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleToggleUser(user.id)}
                      sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>
                          {displayName.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={displayName}
                        secondary={
                          raciTaskCount > 0
                            ? `${user.email} · ${raciTaskCount} task assignment${raciTaskCount !== 1 ? 's' : ''}`
                            : user.email
                        }
                        primaryTypographyProps={{ fontWeight: isSelected ? 500 : 400 }}
                      />
                      {user.role && (
                        <Chip label={user.role} size="small" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Selected: {selectedUserIds.size} member{selectedUserIds.size !== 1 ? 's' : ''}
            </Typography>
            {hasChanges && (
              <Typography variant="caption" color="primary">
                Unsaved changes
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || updateProjectMutation.isPending}
            startIcon={
              updateProjectMutation.isPending ? <CircularProgress size={16} /> : undefined
            }
          >
            {updateProjectMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <MemberReassignmentDialog
        open={open && replacementStepOpen}
        pendingRemovals={pendingRemovals}
        replacements={replacements}
        onReplacementsChange={setReplacements}
        candidateUserIds={Array.from(selectedUserIds)}
        usersById={usersById}
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage(null)}
        onBack={() => setReplacementStepOpen(false)}
        onConfirm={handleConfirmReplacements}
        isSubmitting={updateProjectMutation.isPending}
      />
    </>
  );
};

export default AddTeamMemberDialog;
